import { useState } from 'react'

import { Pagination } from '@/components/common'
import { Modal } from '@/components/common/Modal'
import { AdmissionTestForm } from '@/components/forms'
import { useDisclosure } from '@/components/nextui'
import { useAuthStore } from '@/store/authStore'
import { FormsProps } from '@/types/forms/formStatus'

// Define a type for the AdmissionTestForm props based on what it expects
interface AdmissionTestFormCustomProps {
    onTotalPages: (length: number) => void
    currentPage: number
    onPageChange: (page: number) => void
    onNext: () => void
    onPrevious: () => void
}

// Create a local interface that extends AdmissionTestForm's required props
interface AboutYouModuleProps extends FormsProps {
    // Add any additional props if needed
}

export const AboutYouModule = ({
    onNext,
    onPrevious,
    idLastForm,
    completed
}: FormsProps): JSX.Element => {
    const [currentTestPage, setCurrentTestPage] = useState<number>(
        idLastForm || 1
    )
    const [totalTestPages, setTotalTestPages] = useState<number>(1)
    const [isTransitioning, setIsTransitioning] = useState<boolean>(false)
    const { inscriptionStatusId } = useAuthStore()

    // Verificar si el aspirante está admitido/matriculado (inscriptionStatusId === 3, 4 o 6)
    const isBlocked =
        inscriptionStatusId === 3 ||
        inscriptionStatusId === 4 ||
        inscriptionStatusId === 6

    const { isOpen, onClose, onOpen } = useDisclosure({
        defaultOpen: true
    })

    const handleBlockedClick = () => {
        if (isBlocked) {
            onOpen()
        }
    }

    const onNextPage = (): void => {
        if (isTransitioning) {
            return
        }

        if (currentTestPage < totalTestPages) {
            // If there are more test pages, increment currentTestPage
            setCurrentTestPage((prev) => prev + 1)
        } else {
            // If no more test pages, move to next module
            onNext()
        }

        // Scroll after state update
        requestAnimationFrame(() => {
            scroll(0, 100)
        })

        // Reset transition lock
        setTimeout(() => {
            setIsTransitioning(false)
        }, 500)
    }

    const previousPage = (): void => {
        if (isTransitioning) {
            return
        }

        if (currentTestPage > 1) {
            // If not on first page, go to previous page
            setCurrentTestPage((prev) => prev - 1)
        } else {
            // If on first page, go to previous module
            onPrevious()
        }

        setTimeout(() => {
            setIsTransitioning(false)
        }, 500)
    }

    const uploadTotalPages = (length: number): void => {
        setTotalTestPages(length)
    }

    return (
        <div className={`relative ${isBlocked ? 'opacity-60' : ''}`}>
            {isBlocked && (
                <>
                    <div className="absolute inset-0 z-10 bg-gray-200 bg-opacity-50 cursor-not-allowed" />
                    <div
                        className="absolute inset-0 z-20 bg-transparent cursor-pointer"
                        onClick={handleBlockedClick}
                    />
                    <div className="absolute top-2 right-2 z-30 bg-yellow-100 border border-yellow-400 text-yellow-800 px-2 py-1 rounded text-xs">
                        🔒 Campos bloqueados - Click para más información
                    </div>
                </>
            )}
            <section className="space-y-2">
                <Modal
                    isOpen={isOpen}
                    onClose={onClose}
                    text={
                        isBlocked
                            ? 'inscriptionDataChange'
                            : 'AdmissionTest.modal'
                    }
                />
                <AdmissionTestForm
                    {...({
                        onNext: onNextPage,
                        onPrevious: previousPage,
                        onTotalPages: uploadTotalPages,
                        currentPage: currentTestPage,
                        onPageChange: setCurrentTestPage
                    } as unknown as AdmissionTestFormCustomProps)}
                />
                <Pagination
                    currentPage={currentTestPage}
                    totalPages={totalTestPages}
                    completed={completed}
                />
            </section>
        </div>
    )
}
